import React, { useEffect, useState } from 'react';
import { Col, Row, Spin, Tabs } from 'antd';
import { useQuery } from 'react-query';
import { BarChartOutlined, TableOutlined } from '@ant-design/icons';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import chroma from 'chroma-js';
import { DocService } from '../../../services/dataService';
import SearchFilter from '../dashboard/components/filter';
import { useFilters } from '../../../hooks/filters';
import DrugScoringTable from './DrugScoringTable';

const { TabPane } = Tabs;

const KEY_NAMES = {
  tissue: 'disease.raw',
  drug: 'drug.raw',
  combination: 'is_combination_therapy.raw',
  study_type: 'study_type.raw',
  ta: 'impact_type.raw',
  relevant: 'is_relevant.raw',
  published_year: 'published_year',
};
const generateColors = (n) => {
  // return chroma.scale(['#115f9a', '#d0f400']).colors(n);
  // return chroma.scale([ '#9080ff', '#ffb400']).colors(n);
  return chroma.scale(['#e27c7c', '#a86464', '#599e94', '#6cd4c5']).colors(n);
};

const StatsBarChart = ({ data, selectedKey, breakdownKey, onFilterAdd }) => {
  let uniqueKeys = [];
  selectedKey = KEY_NAMES[selectedKey];

  let process_data = data.map((item) => {
    let temp = {
      // name: item[breakdownKey],
      name: item['key'],
    };
    console.log('Selected Key', selectedKey, item[selectedKey]);
    item[selectedKey].forEach((element) => {
      temp[element['key']] = element['doc_count'];

      // Add unique keys
      if (!uniqueKeys.includes(element['key'])) {
        uniqueKeys.push(element['key']);
      }
    });
    return temp;
  });

  let colors = generateColors(uniqueKeys.length);

  process_data = process_data.slice(0, 10);
  return (
    <div className="h-[450px] w-full pb-8">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          width={500}
          height={300}
          data={process_data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
          layout="vertical"
          barSize={12}
          barGap={24}
          onClick={(e) => console.log(e)}
        >
          {uniqueKeys.length <= 10 && (
            <Legend
              verticalAlign="top"
              iconSize={10}
              wrapperStyle={{
                fontSize: '14px',
              }}
            />
          )}

          <XAxis type="number" hide />
          <YAxis dataKey="name" type="category" axisLine={false} width={150} tick={{ fontSize: 14 }} />
          <Tooltip />
          {uniqueKeys.map((key) => {
            return (
              <Bar
                key={key}
                dataKey={key}
                onClick={(e) => {
                  onFilterAdd({
                    key: key.replace('.raw', ''),
                    breakdownKey: e.name.replace('.raw', ''),
                  });
                }}
                stackId="a"
                fill={colors[uniqueKeys.indexOf(key)]}
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const ChartsView = () => {
  const { search, filters, setFilters } = useFilters();
  const stats = useQuery(['tissueStats', search, filters], () => {
    return DocService.getAggregatedData({
      filters: JSON.stringify(filters),
      search,
    });
  });

  useEffect(() => {}, [filters]);

  return (
    <div className="">
      <div className="mx-auto">
        <div className="my-4">
          <SearchFilter />
        </div>
        <div className="mx-4">
          <div className="text-lg font-semibold mt-12 mb-8">Tissue Breakdown</div>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <div className="p-4 border shadow-sm rounded-md bg-white ">
                <div className="text-[0.925rem] text-gray-600 ">Study Type</div>
                {stats.data?.[KEY_NAMES['tissue']] ? (
                  <StatsBarChart
                    data={stats.data?.[KEY_NAMES['tissue']]}
                    selectedKey={'study_type'}
                    breakdownKey={KEY_NAMES.tissue}
                    onFilterAdd={(e) => {
                      // addFilter("extraction.study_type", e.name);
                      setFilters((prev) => ({
                        ...prev,
                        [KEY_NAMES.tissue.replace('.raw', '')]: e.breakdownKey,
                        [KEY_NAMES.study_type.replace('.raw', '')]: e.key,
                      }));
                    }}
                  />
                ) : null}
              </div>
            </Col>
            <Col span={12}>
              <div className="p-4 border shadow-sm rounded-md bg-white ">
                <div className="text-[0.925rem] text-gray-600 ">Therapeutic Association</div>

                {stats.data?.[KEY_NAMES['tissue']] ? (
                  <StatsBarChart
                    data={stats.data?.[KEY_NAMES['tissue']]}
                    selectedKey={'ta'}
                    breakdownKey={KEY_NAMES.tissue}
                    onFilterAdd={(e) => {
                      setFilters((prev) => ({
                        ...prev,
                        [KEY_NAMES.tissue.replace('.raw', '')]: e.breakdownKey,
                        [KEY_NAMES.ta.replace('.raw', '')]: e.key,
                      }));
                    }}
                  />
                ) : null}
              </div>
            </Col>
          </Row>
          <div className="text-xl font-semibold mt-24 mb-8">Drug Breakdown</div>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <div className="p-4 border shadow-sm rounded-md bg-white ">
                <div className="text-[0.925rem] text-gray-600 ">Study Type</div>
                {stats.data?.[KEY_NAMES['drug']] ? (
                  <StatsBarChart
                    data={stats.data?.[KEY_NAMES['drug']]}
                    selectedKey={'study_type'}
                    breakdownKey={KEY_NAMES.drug}
                    onFilterAdd={(e) => {
                      setFilters((prev) => ({
                        ...prev,
                        [KEY_NAMES.study_type.replace('.raw', '')]: e.key,
                        [KEY_NAMES.drug.replace('.raw', '')]: e.breakdownKey,
                      }));
                    }}
                  />
                ) : null}
              </div>
            </Col>
            <Col span={12}>
              <div className="p-4 border shadow-sm rounded-md bg-white ">
                <div className="text-[0.925rem] text-gray-600 ">Therapeutic Association</div>

                {stats.data?.[KEY_NAMES['drug']] ? (
                  <StatsBarChart
                    data={stats.data?.[KEY_NAMES['drug']]}
                    selectedKey={'ta'}
                    breakdownKey={KEY_NAMES.drug}
                    onFilterAdd={(e) => {
                      setFilters((prev) => ({
                        ...prev,
                        [KEY_NAMES.drug.replace('.raw', '')]: e.breakdownKey,
                        [KEY_NAMES.ta.replace('.raw', '')]: e.key,
                      }));
                    }}
                  />
                ) : null}
              </div>
            </Col>
            <Col span={12}>
              <div className="p-4 border shadow-sm rounded-md bg-white ">
                <div className="text-[0.925rem] text-gray-600 ">Publication Year</div>

                {stats.data?.[KEY_NAMES['drug']] ? (
                  <StatsBarChart
                    data={stats.data?.[KEY_NAMES['drug']]}
                    selectedKey={'published_year'}
                    breakdownKey={KEY_NAMES.drug}
                    onFilterAdd={(e) => {
                      setFilters((prev) => ({
                        ...prev,
                        [KEY_NAMES.drug.replace('.raw', '')]: e.breakdownKey,
                        [KEY_NAMES.published_year.replace('.raw', '')]: e.key,
                      }));
                    }}
                  />
                ) : null}
              </div>
            </Col>
            <Col span={12}>
              <div className="p-4 border shadow-sm rounded-md bg-white ">
                <div className="text-[0.925rem] text-gray-600 ">Tissue Breakdown</div>

                {stats.data?.[KEY_NAMES['drug']] ? (
                  <StatsBarChart
                    data={stats.data?.[KEY_NAMES['drug']]}
                    selectedKey={'tissue'}
                    breakdownKey={KEY_NAMES.drug}
                    onFilterAdd={(e) => {
                      setFilters((prev) => ({
                        ...prev,
                        [KEY_NAMES.drug.replace('.raw', '')]: e.breakdownKey.replace('.raw', ''),
                        [KEY_NAMES.tissue.replace('.raw', '')]: e.key.replace('.raw', ''),
                      }));
                    }}
                  />
                ) : null}
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

const ScoringView = () => {
  return (
    <div className="p-4">
      <Tabs defaultActiveKey="1" type="card" size="large">
        <TabPane
          tab={
            <span>
              <TableOutlined />
              Drug Scoring Table
            </span>
          }
          key="1"
        >
          <DrugScoringTable />
        </TabPane>
        <TabPane
          tab={
            <span>
              <BarChartOutlined />
              Analytics Charts
            </span>
          }
          key="2"
        >
          <ChartsView />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default ScoringView;
